import { useMemo } from 'react';
import { atomFamily, selectorFamily, atom, useRecoilValue, DefaultValue } from 'recoil';
import type { RecoilState, RecoilValue, RecoilValueReadOnly } from 'recoil';
import { logger } from 'modules/logger';
import type {
  ViewportsConfigurations,
  ViewportConfiguration,
  StudyGroup,
  ViewportDisplayConfiguration,
} from './types';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
  localForagePersisterEffect,
} from 'utils/recoilEffects';
import { generateViewportsConfigurations } from './factories';
import {
  activeStepBucketSelector,
  isPreviewingHangingProtocolState,
} from '../HangingProtocol/state';
import { FLAT_BUCKET, mergeBucketsFromBroadcast, readBucket } from './stepBuckets';
import type { BucketedViewports } from './stepBuckets';
import { clearStepViewportOverrides, setStepViewportOverrides } from './viewportStepWriters';
import {
  layoutsForAllCasesState,
  DEFAULT_LAYOUTS,
} from 'domains/viewer/Viewer/StudyLoader/viewerLoaderState';
import { openedViewersState } from 'domains/viewer/TrackOpenedViewers';
import { useStudies } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { activeViewportState } from 'config/recoilState';
import {
  dehydrateViewportsConfigurations,
  hydrateViewportConfiguration,
  hydrateViewportsConfigurations,
} from './manipulators';
import { NOT_FOUND_CASE_ID, type ViewportTypeKeys } from 'config/constants';
import equals from 'ramda/es/equals';
import type { VtkAnnotation, Segment } from '../ViewportDre/Annotations/types';
import type { Layouts } from '../Viewer/StudyLoader/viewerLoaderState';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import type { ScaleFactorTags } from '../ViewportDre/utils/math';
import { ViewType } from 'generated/graphql';
import { WithApolloContext } from 'modules/Apollo/types';

type ViewportsConfigurationsState = {
  created: Nullable<number>;
  updated: Nullable<number>;
  /**
   * User viewport overrides, partitioned by the clinical step they were made on.
   *
   * Was a single `data` map keyed only by case, which is why an edit on one step showed up on every
   * other one -- the VX-4624 family, fifth instance. The partition is what makes that structurally
   * impossible rather than defended against at each write site.
   */
  byStep: BucketedViewports;
};

type GeneratorArgs = {
  defaults?: Partial<ViewportConfiguration>;
  groupedStudies: StudyGroup[];
};

type ViewportsConfigurationsSelectorArgs = Readonly<{
  caseSmid: Nullable<string>;
  generatorArgs: GeneratorArgs;
  toJSON: () => Record<any, any> | string;
}>;

type ViewportConfigurationSelectorArgs = Readonly<{
  caseSmid: Nullable<string>;
  viewportId: string;
  generatorArgs: GeneratorArgs;
  toJSON: () => Record<any, any> | string;
}>;

/**
 * This state is responsible of storing any user-defined viewport configurations.
 * We don't need to store anything that is automatically generated since that's
 * predictable and can be regenerated from the data.
 * This atom also includes two timestamps (created and updated) to help us
 * delete old configurations that are no longer needed.
 *
 * TODO: implement logic to automatically purge old configurations.
 */
/**
 * Merges an incoming broadcast's step buckets over the local ones instead of replacing the record.
 *
 * See {@link mergeBucketsFromBroadcast} for why absence is not authoritative. `updated` is taken from
 * the incoming value: it stamps when the *sender* last wrote, which is what the retention sweep wants,
 * and the merge only ever adds buckets the sender did not know to mention.
 *
 * Declared above the atom rather than beside its sibling below because a `const` is not hoisted and
 * the atom reads it during module evaluation.
 */
export const mergeViewportOverridesFromBroadcastMergeFn = (
  oldValue: Nullable<ViewportsConfigurationsState>,
  newValue: ViewportsConfigurationsState
): ViewportsConfigurationsState => ({
  ...newValue,
  byStep: mergeBucketsFromBroadcast(oldValue?.byStep, newValue?.byStep),
});

// The generic is explicit so the `default` literal cannot narrow it. Inferring it made `created`
// `number` rather than `Nullable<number>`, which is what the `@ts-expect-error` removed here used to
// paper over -- and it broke the moment a typed `mergeFn` had to agree with the atom's own type.
export const viewportsConfigurationsState: (
  caseSmid: string
) => RecoilState<ViewportsConfigurationsState> = atomFamily<ViewportsConfigurationsState, string>({
  key: 'viewportsConfigurations',
  default: {
    created: Date.now(),
    updated: null,
    byStep: {},
  },
  effects: [
    broadcastChannelSynchronizerEffect({ mergeFn: mergeViewportOverridesFromBroadcastMergeFn }),
    localForagePersisterEffect({
      // v3: `data` became `byStep`. A v2 record has no step attribution to recover, so it is dropped
      // rather than migrated -- the worst case is a user losing viewport drops they made before this
      // deploy, which the next drop re-establishes.
      version: 3,
      maxKeys: 250,
    }),
  ],
});

export const generateViewportsConfigurationsSelectorArgs = ({
  caseSmid,
  generatorArgs,
}: Omit<ViewportsConfigurationsSelectorArgs, 'toJSON'>): ViewportsConfigurationsSelectorArgs => ({
  caseSmid,
  generatorArgs,
  toJSON: () => {
    const { defaults, groupedStudies } = generatorArgs;
    return JSON.stringify({
      caseSmid,
      defaults,
      groupedStudies: groupedStudies.map((gs) =>
        gs.map(
          (s) =>
            `${s.smid}:${s.seriesList.map((sl) => sl.smid).join(',')}:${s.stackedFrames
              .map((si) => si.smid)
              .join(',')}`
        )
      ),
    });
  },
});

export const generateViewportConfigurationSelectorArgs = ({
  caseSmid,
  viewportId,
  generatorArgs,
}: Omit<ViewportConfigurationSelectorArgs, 'toJSON'>): ViewportConfigurationSelectorArgs => ({
  caseSmid,
  viewportId,
  generatorArgs,
  toJSON: () => {
    const { defaults, groupedStudies } = generatorArgs;
    return JSON.stringify({
      caseSmid,
      defaults,
      viewportId,
      groupedStudies: groupedStudies.map((gs) =>
        gs.map(
          (s) =>
            `${s.smid}:${s.seriesList.map((sl) => sl.smid).join(',')}:${s.stackedFrames
              .map((si) => si.smid)
              .join(',')}`
        )
      ),
    });
  },
});

/*
 * Same as viewportsConfigurationsState but only returns the actual data
 * and takes care of updating the timestamps when mutations happen.
 * It also allows to pass a nullish case smid since our `useCaseSmid` hook
 * can return such datum.
 */
export const viewportsConfigurationsSelector: (
  args: ViewportsConfigurationsSelectorArgs
) => RecoilState<Nullable<ViewportsConfigurations>> = selectorFamily({
  key: 'viewportsConfigurationsSelector',
  get:
    ({ caseSmid, generatorArgs }) =>
    ({ get }) => {
      if (caseSmid == null) {
        return null;
      }

      const { defaults, groupedStudies } = generatorArgs;

      const isPreviewingHangingProtocol = get(isPreviewingHangingProtocolState);

      // the layoutsForAllCasesState only stores layouts set by the user
      // if there are no layouts set by the user, we use the ones defined
      // in the hanging protocol, if there are no layouts defined in the
      // hanging protocol, we use the default layouts
      // during HP preview, the user-set layout must not filter which HP viewports are populated
      const caseLayouts: Nullable<Layouts> = isPreviewingHangingProtocol
        ? null
        : get(layoutsForAllCasesState)[caseSmid];
      const hangingProtocolLayouts: Nullable<Layouts> = get(hangingProtocolLayoutsState);
      const layouts = caseLayouts ?? hangingProtocolLayouts ?? DEFAULT_LAYOUTS;

      const windows = get(openedViewersState);
      // A null bucket means init has not decided yet. Reading the flat bucket here would show a
      // stepped protocol's user overrides for one frame before the real step arrives, so read nothing.
      const bucket = get(activeStepBucketSelector);
      const dehydratedViewportsConfigurations =
        bucket == null
          ? null
          : readBucket(get(viewportsConfigurationsState(caseSmid)).byStep, bucket);
      const dehydratedHangingProtocolViewportsConfiguration =
        bucket == null ? null : readBucket(get(dehydratedHangingProtocol(caseSmid)).byStep, bucket);
      const viewportsConfigurations =
        dehydratedViewportsConfigurations != null
          ? hydrateViewportsConfigurations(dehydratedViewportsConfigurations, groupedStudies)
          : null;
      const hangingProtocolViewportsConfiguration =
        dehydratedHangingProtocolViewportsConfiguration != null
          ? hydrateViewportsConfigurations(
              dehydratedHangingProtocolViewportsConfiguration,
              groupedStudies
            )
          : null;

      return generateViewportsConfigurations({
        defaults,
        groupedStudies,
        layouts,
        windows,
        viewportsConfigurations,
        hangingProtocolViewportsConfiguration,
        isPreviewingHangingProtocol,
      });
    },
  set:
    ({ caseSmid }) =>
    ({ get, set }, newData: Nullable<DefaultValue | ViewportsConfigurations>) => {
      if (caseSmid == null) return;
      const bucket = get(activeStepBucketSelector);
      // A write arriving before HP init settles has no step to attribute itself to, so it is dropped
      // rather than misfiled into the flat bucket -- filing it there would surface it on every step
      // and then lose it the moment the real steps land, which is the failure this slice exists to
      // prevent. Dropping is the lesser evil, not a good outcome: the window is between first paint
      // and the init effect, so a human drag cannot realistically land in it, but a programmatic
      // write on mount can. Logged rather than swallowed so a lost edit is diagnosable instead of
      // silent. If this ever fires in practice the fix is to queue the write until the bucket
      // resolves, not to widen the fallback.
      if (bucket == null) {
        logger.warn(
          '[ViewportsConfigurations] dropped a viewport write before the step bucket resolved',
          {
            caseSmid,
            isReset: newData instanceof DefaultValue,
          }
        );
        return;
      }
      if (newData instanceof DefaultValue) {
        clearStepViewportOverrides(set, { caseId: caseSmid, bucket });
        return;
      }
      setStepViewportOverrides(set, {
        caseId: caseSmid,
        bucket,
        overrides: newData != null ? dehydrateViewportsConfigurations(newData) : null,
      });
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

/**
 * This state holds the information needed to adjust the rendered image
 * according to the user-provided settings and the one that have been
 * automatically defined by our logic.
 * It's stored separately and keyed by the case smid and the view type
 * so that we can leave them around and retrieve them later if needed.
 */
type ViewportDisplayConfigurationState = {
  created: Nullable<number>;
  updated: Nullable<number>;
  data: Nullable<ViewportDisplayConfiguration>;
};

// @ts-expect-error [EN-10263] - TS2322 - Type '(param: { stackSmid: string; viewType: ViewportTypeKeys; viewportId: string; }) => RecoilState<{ created: number; updated: null; data: null; }>' is not assignable to type '(arg1: { stackSmid: string; viewType: ViewportTypeKeys; viewportId: string; }) => RecoilState<ViewportDisplayConfigurationState>'.
export const viewportDisplayConfigurationState: (arg1: {
  stackSmid: string;
  viewType: ViewportTypeKeys;
  viewportId: string;
}) => RecoilState<ViewportDisplayConfigurationState> = atomFamily({
  key: 'viewportDisplayConfiguration',
  default: {
    created: Date.now(),
    updated: null,
    data: null,
  },
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      maxKeys: 500,
    }),
  ],
});

const viewportDisplayConfigurationsIdsState = atom<string[]>({
  key: 'viewer.viewportDisplayConfigurationsIdsState',
  default: [],
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
});

export const viewportDisplayHungStacksIdsState: RecoilState<string[]> = atom<string[]>({
  key: 'viewer.dre.viewportDisplayHungStacksIdsState',
  default: [],
  effects: [broadcastChannelSynchronizerEffect({ unidirectional: true })],
});

export const mostRecentMatchingViewportDisplayConfigurationSelector: (arg1: {
  stackSmid: Nullable<string>;
  viewType: Nullable<ViewportTypeKeys>;
}) => RecoilValueReadOnly<Nullable<ViewportDisplayConfiguration>> = selectorFamily({
  key: 'viewer.mostRecentMatchingViewportDisplayConfigurationSelector',
  get:
    ({ stackSmid, viewType }) =>
    ({ get }) => {
      if (stackSmid == null || viewType == null) return null;

      const viewportDisplayConfigurationsIds = get(viewportDisplayConfigurationsIdsState);

      const viewportDisplayConfigurations: ViewportDisplayConfigurationState[] =
        viewportDisplayConfigurationsIds
          .map((viewportId) =>
            get(viewportDisplayConfigurationState({ stackSmid, viewType, viewportId }))
          )
          .filter(Boolean);

      const mostRecentViewportDisplayConfiguration: Nullable<ViewportDisplayConfigurationState> =
        viewportDisplayConfigurations.sort((a, b) => {
          return (b.updated ?? 0) - (a.updated ?? 0);
        })[0];

      return mostRecentViewportDisplayConfiguration?.data;
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const viewportDisplayConfigurationSelector: (
  arg1:
    | {
        stackSmid: Nullable<string>;
        viewType: Nullable<ViewportTypeKeys>;
        viewportId: Nullable<string>;
      }
    | undefined
) => RecoilState<Nullable<ViewportDisplayConfiguration>> = selectorFamily({
  key: 'viewportDisplayConfigurationSelector',
  get:
    (payload) =>
    ({ get }) => {
      const { stackSmid, viewType, viewportId } = payload || {};
      if (stackSmid == null || viewType == null || viewportId == null) return null;
      const exactViewportDisplayConfiguration = get(
        viewportDisplayConfigurationState({ stackSmid, viewType, viewportId })
      ).data;

      let viewportDisplayConfiguration;

      if (exactViewportDisplayConfiguration != null) {
        viewportDisplayConfiguration = exactViewportDisplayConfiguration;
      } else {
        viewportDisplayConfiguration = get(
          mostRecentMatchingViewportDisplayConfigurationSelector({ stackSmid, viewType })
        );
      }

      return viewportDisplayConfiguration;
    },
  set:
    (payload) =>
    ({ set, get }, newData: Nullable<DefaultValue | ViewportDisplayConfiguration>) => {
      const { stackSmid, viewType, viewportId } = payload || {};
      if (stackSmid == null || viewType == null || viewportId == null) return;

      set(viewportDisplayConfigurationsIdsState, (state) =>
        Array.from(new Set([...state, viewportId]))
      );

      if (newData instanceof DefaultValue) {
        set(viewportDisplayConfigurationState({ stackSmid, viewType, viewportId }), (state) => ({
          created: state.created,
          updated: Date.now(),
          data: null,
        }));
      } else {
        set(viewportDisplayConfigurationState({ stackSmid, viewType, viewportId }), (state) => {
          // if the new data is equal to the stored data, we don't need to update
          // the timestamp as the data itself did not change, only exception is
          // when the state is a DefaultValue, in which case we want to update
          // the atom value so that it loses its DefaultValue status
          if (!(state instanceof DefaultValue) && equals(state.data, newData)) {
            return state;
          }

          return {
            created: state.created,
            updated: Date.now(),
            data: newData,
          };
        });
      }
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

/*
 * Here we store the resulting ViewportsConfigurations obtained by resolving
 * the currently selected Hanging Protocol.
 */
export const hangingProtocolViewportConfigurationSelector: (
  arg1: ViewportConfigurationSelectorArgs
) => RecoilValueReadOnly<Nullable<ViewportConfiguration>> = selectorFamily({
  key: 'hangingProtocolViewportConfiguration',
  get:
    ({ caseSmid, viewportId, generatorArgs }) =>
    ({ get }) => {
      const { groupedStudies } = generatorArgs;
      if (caseSmid == null) return null;
      const bucket = get(activeStepBucketSelector);
      if (bucket == null) return null;
      const viewportConfiguration = readBucket(
        get(dehydratedHangingProtocol(caseSmid)).byStep,
        bucket
      )?.[viewportId];

      if (viewportConfiguration == null) {
        return null;
      }

      analytics.addViewContext(`${globalContext.viewer.viewportConfigurations}.${viewportId}`, {
        seriesSmid: viewportConfiguration?.series?.smid,
        stackSmid: viewportConfiguration?.stack?.smid,
        viewType: viewportConfiguration?.viewType,
      });

      return hydrateViewportConfiguration(viewportConfiguration, groupedStudies);
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

type DehydratedHangingProtocolState = {
  /** The resolved HP viewport assignments, partitioned by the step they were resolved for. */
  byStep: BucketedViewports;
};

/**
 * Reject an incoming broadcast that carries no buckets at all when we already hold some.
 *
 * Bucketing changed the shape of the stale-value hazard, not its existence. The predecessor of this
 * function rejected an incoming `null`; the value can no longer be null, but its *empty* form is now
 * `{ byStep: {} }`, and that is what a fresh or stale hydration produces. The race is unchanged:
 *   1. Window A populates the HP atom for case X with real data.
 *   2. Window B (secondary viewer, same case X) subscribes later. Its persister reads a stale or
 *      absent record for case X and fires setSelf with the empty default.
 *   3. `broadcastChannelSynchronizerEffect` posts on every onSet, including a persister-driven one,
 *      so B's empty value is relayed to A.
 *   4. Window A's HP atom is clobbered; viewports go empty.
 * The `ready` handshake does not prevent this, because B's hydration can broadcast before A's
 * persisted write lands.
 *
 * "No buckets at all" is the discriminator rather than "no populated buckets", and the distinction is
 * load-bearing: a deliberate clear writes a bucket holding `{}`, which is real state and must be
 * allowed to propagate. Only a record with zero keys is the empty sentinel this rejects.
 * reset() / DefaultValue paths do not flow through mergeFn.
 */
export const mergeByStepFromBroadcastMergeFn = (
  oldValue: Nullable<DehydratedHangingProtocolState>,
  newValue: DehydratedHangingProtocolState
): DehydratedHangingProtocolState => ({
  ...newValue,
  byStep: mergeBucketsFromBroadcast(oldValue?.byStep, newValue?.byStep, {
    rejectLessResolved: true,
  }),
});

export const dehydratedHangingProtocol = atomFamily<DehydratedHangingProtocolState, string>({
  key: 'dehydratedHangingProtocol',
  default: { byStep: {} },
  effects: [
    broadcastChannelSynchronizerEffect({ mergeFn: mergeByStepFromBroadcastMergeFn }),
    localForagePersisterEffect({
      // v3: the value became bucketed. See the note on viewportsConfigurationsState.
      version: 3,
    }),
  ],
});

/**
 * Which series each viewport of the **active step** currently holds, keyed by viewport id.
 *
 * Exists for the hanging-protocol save path, which has to decide mirror groups -- "hang one shared
 * stack in these viewports" -- and cannot decide them from what it stores. A protocol is stored
 * criteria-only, and two viewports holding genuinely different series routinely carry identical
 * criteria, because the form classifies by series description and a study can hold two series with
 * one description. Grouping on criteria therefore cannot separate "one series hung twice" from "two
 * series that classify alike" (VX-5005 / VX-5006). What the viewer has in each viewport can.
 *
 * Deliberately dehydrated: it reads the same two bucketed atoms as
 * {@link viewportsConfigurationsSelector}, with user overrides winning over the protocol's own
 * assignments, but skips `hydrateViewportsConfigurations` entirely. That keeps it free of
 * `groupedStudies` -- and so of `useStudies`, `useCurrentCaseId` and the router context those drag in
 * -- which matters because its one consumer is the HP form, a component mounted in tests without a
 * `<Router>`. The smids are all this question needs.
 *
 * An empty record means "nothing known", including while the step bucket is still unresolved. Callers
 * treat that as "leave existing mirror groups alone" rather than as "clear them".
 */
/** Which series each viewport of `bucket` holds, protocol assignments with user overrides layered on. */
function readBucketSeriesIds(
  get: <T>(recoilValue: RecoilValue<T>) => T,
  caseSmid: string,
  bucket: string
): Record<string, string | undefined> {
  const fromProtocol = readBucket(get(dehydratedHangingProtocol(caseSmid)).byStep, bucket);
  const overrides = readBucket(get(viewportsConfigurationsState(caseSmid)).byStep, bucket);

  const seriesIds: Record<string, string | undefined> = {};
  // Overrides second so they win, and win even when they resolve to nothing: a viewport the user
  // cleared must stop counting as a mirror-group member.
  //
  // That last part needs the `null` case handled rather than skipped, and it used to be skipped --
  // the comment promised this behaviour while `if (config == null) continue` did the opposite, so a
  // viewport cleared through `viewportConfigurationSelector` (which records a clear as an explicit
  // `null` override) kept whatever series the protocol had assigned it, and went on counting as a
  // mirror-group member for the save. Recording `undefined` is what "known to hold nothing" looks
  // like to every consumer: `assignMirrorGroupIds` drops it to the criteria tier, and
  // `hasNoResolvedSeries` reads an all-unknown map as "no evidence, leave existing groups alone".
  for (const source of [fromProtocol, overrides]) {
    for (const [viewportId, config] of Object.entries(source ?? {})) {
      // Falls back to the stack for view types that carry no series reference of their own; the
      // key only has to be stable and shared by viewports showing the same thing.
      seriesIds[viewportId] = config?.series?.smid ?? config?.stack?.smid ?? undefined;
    }
  }
  return seriesIds;
}

export const activeStepViewportSeriesIdsSelector: (
  caseSmid: Nullable<string>
) => RecoilValueReadOnly<Readonly<Record<string, string | undefined>>> = selectorFamily({
  key: 'activeStepViewportSeriesIds',
  get:
    (caseSmid) =>
    ({ get }) => {
      if (caseSmid == null) return {};
      const bucket = get(activeStepBucketSelector);
      if (bucket == null) return {};
      return readBucketSeriesIds(get, caseSmid, bucket);
    },
});

/**
 * The same question asked of the **flat** bucket, which is where a viewport edit made before any
 * step existed is filed.
 *
 * Needed because {@link activeStepViewportSeriesIdsSelector} cannot answer at create time. Creating a
 * protocol from the Default HP mints a brand-new step, so the active bucket is that step's and it
 * holds nothing yet -- while everything the reader hung beforehand sits under `FLAT_BUCKET`. On a
 * two-window viewer that is the *only* record of what window 1 is showing, because the HP form
 * renders the primary window's grid alone and so has no form state for those viewports either. With
 * no answer, they save with no criteria and the backend resolves them by description or by the
 * remaining-series fill, which is VX-5125.
 *
 * Lowest precedence at the call site: an active-step answer is a newer statement of the same fact.
 */
export const flatBucketViewportSeriesIdsSelector: (
  caseSmid: Nullable<string>
) => RecoilValueReadOnly<Readonly<Record<string, string | undefined>>> = selectorFamily({
  key: 'flatBucketViewportSeriesIds',
  get:
    (caseSmid) =>
    ({ get }) => {
      if (caseSmid == null) return {};
      return readBucketSeriesIds(get, caseSmid, FLAT_BUCKET);
    },
});

/*
 * Here we store the layouts that are available for the currently selected Hanging Protocol.
 */
// @ts-expect-error [EN-10263] - TS2322 - Type 'RecoilState<null>' is not assignable to type 'RecoilState<Layouts | null | undefined>'.
export const hangingProtocolLayoutsState: RecoilState<Nullable<Layouts>> = atom({
  key: 'viewer.hangingProtocolLayoutsState',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      version: 1,
    }),
  ],
});

/*
 * Stores the active hanging protocol SMID.
 * Used by StepBar for clinical step operations (add, reorder) and
 * isDefaultHP detection. Also used for analytics tracking.
 *
 * The merge function rejects stale null echoes from BroadcastChannel.
 * Without this, the atom briefly resets to null when a stale broadcast
 * arrives between the effect setting it and the next render, causing
 * createStepOnBackend and saveStepOrder to silently skip backend calls.
 */
export const activeHangingProtocolSmidState = atom<string | null>({
  key: 'viewer.activeHangingProtocolSmidState',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect({
      mergeFn: (oldValue: Nullable<string>, newValue: string | null) => {
        // Reject a null incoming value when local has one (stale echo). When the
        // incoming value is null and local is also null the result is the same,
        // so returning oldValue covers both cases (normalizing undefined to null
        // to satisfy the atom's string | null type). Otherwise accept the incoming.
        return newValue == null ? (oldValue ?? null) : newValue;
      },
    }),
  ],
});

/**
 * Here we store the resulting ViewportsConfigurations obtained by resolving
 * the currently previewed Hanging Protocol.
 */
// @ts-expect-error [EN-10263] - TS2322 - Type 'RecoilState<null>' is not assignable to type 'RecoilState<ViewportsConfigurations | null | undefined>'.
export const hangingProtcolPreviewState: RecoilState<Nullable<ViewportsConfigurations>> = atom({
  key: 'hangingProtcolPreviewState',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      version: 1,
    }),
  ],
});

/**
 * This selector allows each viewport to retrieve its own configuration and update it.
 * The selector takes care of merging the new configuration with the rest of the viewer configurations.
 */
/**
 * The map without the viewports the generator could not hydrate.
 *
 * `generateViewportsConfigurations` returns `null` for a viewport whose study is not loaded, which is
 * a statement about *this render*, not about what the reader wants. See the call site for why letting
 * one reach the override atom is VX-5091.
 */
function withoutUnhydratedViewports(state: ViewportsConfigurations): ViewportsConfigurations {
  return Object.fromEntries(
    Object.entries(state ?? {}).filter(([, config]) => config != null)
  ) as ViewportsConfigurations;
}

export const viewportConfigurationSelector: (
  arg1: ViewportConfigurationSelectorArgs
) => RecoilState<Nullable<ViewportConfiguration>> = selectorFamily({
  key: 'viewportConfiguration',
  get:
    ({ caseSmid, viewportId, generatorArgs }) =>
    ({ get }) => {
      const viewportConfiguration = get(
        viewportsConfigurationsSelector(
          generateViewportsConfigurationsSelectorArgs({ caseSmid, generatorArgs })
        )
      )?.[viewportId];

      analytics.addViewContext(`${globalContext.viewer.viewportConfigurations}.${viewportId}`, {
        seriesSmid: viewportConfiguration?.series?.smid,
        stackSmid: viewportConfiguration?.stack?.smid,
        viewType: viewportConfiguration?.viewType,
      });

      return viewportConfiguration;
    },
  set:
    ({ caseSmid, viewportId, generatorArgs }) =>
    ({ get, set }, viewportConfiguration: Nullable<DefaultValue | ViewportConfiguration>) => {
      if (caseSmid == null) {
        console.warn(
          'viewportConfigurationSelector: caseSmid is required while setting a viewport configuration!'
        );
        return;
      }

      const isPreviewing = get(isPreviewingHangingProtocolState);

      // Users are not allowed to make changes to the viewports while previewing a hanging protocol.
      // If they tried to do so, it would be impossible to reset the viewports configurations state
      // to the original state.
      if (isPreviewing) return;

      // Clearing a viewport that is already showing nothing is not a gesture, and must not be
      // recorded as one.
      //
      // Every consumer here holds this selector through `useRecoilState` and writes with an *updater*,
      // which Recoil resolves against the selector's `get` -- the rendered value. So when hydration
      // transiently fails, the rendered value is `null`, an updater of the shape
      // `config != null ? {...config, x} : null` hands back `null`, and that null lands in the
      // override atom as "the reader deliberately emptied this slot". It outranks the protocol base
      // and it is persisted, so a refresh replays it.
      //
      // Deleting a hanging protocol is the window where this bites: the step atoms are cleared and the
      // protocol goes null while the refetch is in flight. Measured on a local stack, with markers
      // around each gesture -- the null writes for `Viewport-0-0-0` and `Viewport-0-0-1` landed
      // strictly between "before delete" and "after delete toast", not during the swap that had been
      // suspected, and those are the two slots the default protocol populates. That is VX-5091, and it
      // is also why clearing the overrides when the delete is *issued* only helped 1 run in 5: the
      // nulls arrive afterwards.
      //
      // A real clear is unaffected: the reader can only clear a viewport that is showing something, so
      // the current value is non-null and this guard does not fire.
      const renderedNow = get(
        viewportsConfigurationsSelector(
          generateViewportsConfigurationsSelectorArgs({ caseSmid, generatorArgs })
        )
      );
      const clearsNothing =
        (viewportConfiguration == null || viewportConfiguration instanceof DefaultValue) &&
        renderedNow?.[viewportId] == null;
      if (clearsNothing) return;

      // Carry the rendered map forward, minus the entries the *generator* nulled.
      //
      // The spread itself is load-bearing and must stay: the map this reads is
      // `generateViewportsConfigurations`' output, so spreading it materialises the protocol's
      // resolved series into the override layer, and that is what makes a clinical viewport edit
      // visible at all. Removing the spread was tried and it broke a viewport swap -- the step write
      // alone carries criteria, not identities, so the swapped viewport rendered nothing.
      //
      // What must not survive the spread is a `null`. The generator writes one in for any viewport
      // whose study failed to hydrate ("Null out viewports whose study failed hydration"), and in the
      // override layer a null is not neutral -- the generator deliberately keeps it ("If the viewport
      // configuration is null we can KEEP it"), so it outranks the protocol base, and this atom is
      // persisted, so a refresh replays it. Deleting a protocol is exactly when hydration fails
      // transiently, so a viewport write landing in that window recorded "these slots are
      // deliberately empty" for slots the reader never touched: VX-5091. Measured on a local stack
      // with a probe on `setStepViewportOverrides` -- two writes, `nulled=Viewport-0-0-0` then
      // `nulled=Viewport-0-0-0,Viewport-0-0-1`, both into the flat bucket, both from this spread, and
      // those are the two slots the default protocol populates.
      //
      // `viewportId`'s own value is applied after the strip, so clearing the viewport you are setting
      // still works. The trade-off: in flat mode a viewport cleared earlier can be repopulated by the
      // next match once an unrelated viewport is written. That is the better failure -- a deliberate
      // clear is one gesture to redo, where the old behaviour blanked untouched viewports and
      // survived a reload. Clinical step mode is unaffected either way: it records a cleared slot by
      // having *no* step entry (`clearViewportInStep`), not by a null override.
      set(
        // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'RecoilState<ViewportsConfigurations | null | undefined>' is not assignable to parameter of type 'RecoilState<ViewportsConfigurations>'.
        viewportsConfigurationsSelector(
          generateViewportsConfigurationsSelectorArgs({ caseSmid, generatorArgs })
        ),
        (state: ViewportsConfigurations) =>
          ({
            ...withoutUnhydratedViewports(state),
            [viewportId]: viewportConfiguration,
          }) as unknown as ViewportsConfigurations
      );
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS: Partial<ViewportConfiguration> = {
  viewType: ViewType.TwoDDre,
};

/**
 * This hook is useful to retrieve the whole viewports configurations configuration.
 *
 * Usage:
 * ```
 * const viewportsConfigurations = useRecoilValue(useViewportsConfigurationsSelector());
 * ```
 */
export function useViewportsConfigurationsSelector({
  defaults = VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS,
  context,
}: {
  defaults?: Partial<ViewportConfiguration>;
} & WithApolloContext): RecoilState<Nullable<ViewportsConfigurations>> {
  const currentCaseId = useCurrentCaseId();
  const { groupedStudies } = useStudies({ context });

  return useMemo(
    () =>
      viewportsConfigurationsSelector(
        generateViewportsConfigurationsSelectorArgs({
          caseSmid: currentCaseId ?? NOT_FOUND_CASE_ID,
          generatorArgs: { defaults, groupedStudies },
        })
      ),
    [currentCaseId, defaults, groupedStudies]
  );
}

type GetViewportConfigurationSelectorProps = {
  viewportId: string;
  defaults?: Partial<ViewportConfiguration>;
  groupedStudies: Array<StudyGroup>;
  currentCaseId: Nullable<string>;
};

export function getViewportConfigurationSelector({
  viewportId,
  defaults = VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS,
  groupedStudies,
  currentCaseId,
}: GetViewportConfigurationSelectorProps): RecoilState<Nullable<ViewportConfiguration>> {
  return viewportConfigurationSelector(
    generateViewportConfigurationSelectorArgs({
      caseSmid: currentCaseId,
      viewportId,
      generatorArgs: {
        defaults: { ...VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS, ...defaults },
        groupedStudies,
      },
    })
  );
}

export function getHPViewportConfigurationSelector({
  viewportId,
  groupedStudies,
  currentCaseId,
}: GetViewportConfigurationSelectorProps): RecoilValueReadOnly<Nullable<ViewportConfiguration>> {
  return hangingProtocolViewportConfigurationSelector(
    generateViewportConfigurationSelectorArgs({
      caseSmid: currentCaseId,
      viewportId,
      generatorArgs: {
        groupedStudies,
      },
    })
  );
}

export function useHPViewportConfigurationSelector({
  viewportId,
  context,
}: {
  viewportId: string;
} & WithApolloContext): RecoilValueReadOnly<Nullable<ViewportConfiguration>> {
  const currentCaseId = useCurrentCaseId();
  const { groupedStudies } = useStudies({ context });

  return useMemo(
    () => getHPViewportConfigurationSelector({ viewportId, groupedStudies, currentCaseId }),
    [currentCaseId, groupedStudies, viewportId]
  );
}

/**
 * This hook is useful to retrieve a specific viewport configuration ensuring
 * the correct default configurations are generated starting from the currently
 * loaded studies.
 *
 * Usage:
 * ```
 * const viewportConfiguration = useRecoilValue(useViewportConfigurationSelector({ viewportId: 'Viewport-0-0-0' }));
 * ```
 */
export function useViewportConfigurationSelector({
  viewportId,
  defaults = VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS,
  context,
}: {
  viewportId: string;
  defaults?: Partial<ViewportConfiguration>;
} & WithApolloContext): RecoilState<Nullable<ViewportConfiguration>> {
  const currentCaseId = useCurrentCaseId();
  const { groupedStudies } = useStudies({ context });

  return useMemo(
    () => getViewportConfigurationSelector({ viewportId, defaults, groupedStudies, currentCaseId }),
    [currentCaseId, defaults, groupedStudies, viewportId]
  );
}

/**
 * Same as `useViewportConfigurationSelector` but automatically returns the
 * guide viewport configuration selector instead of asking for a viewport id.
 */
export function useGuideViewportConfigurationSelector({
  defaults = {},
  context,
}: {
  defaults?: Partial<ViewportConfiguration>;
} & WithApolloContext): RecoilState<Nullable<ViewportConfiguration>> {
  const viewportId = useRecoilValue(activeViewportState);
  return useViewportConfigurationSelector({
    viewportId,
    defaults,
    context,
  });
}

/**
 * This hook is useful to retrieve a specific viewport display configuration.
 *
 * Usage:
 * ```
 * const viewportDisplayConfiguration = useRecoilValue(useViewportDisplayConfigurationSelector({ viewportId: 'Viewport-0-0-0' }));
 * ```
 */
export function useViewportDisplayConfigurationSelector({
  viewportId,
  context,
}: {
  viewportId: string;
} & WithApolloContext): RecoilState<Nullable<ViewportDisplayConfiguration>> {
  const viewportConfiguration = useRecoilValue(
    useViewportConfigurationSelector({ viewportId, context })
  );

  return useMemo(
    () =>
      viewportDisplayConfigurationSelector({
        stackSmid: viewportConfiguration?.stack?.smid,
        viewType: viewportConfiguration?.viewType,
        viewportId,
      }),
    [viewportConfiguration?.stack?.smid, viewportConfiguration?.viewType, viewportId]
  );
}

/**
 * Same as `useViewportDisplayConfigurationSelector` but automatically returns the
 * guide viewport display configuration selector instead of asking for a viewport id.
 */
export function useGuideViewportDisplayConfigurationSelector({
  context,
}: WithApolloContext): RecoilState<Nullable<ViewportDisplayConfiguration>> {
  const viewportId = useRecoilValue(activeViewportState);
  const viewportConfiguration = useRecoilValue(useGuideViewportConfigurationSelector({ context }));
  return viewportDisplayConfigurationSelector({
    stackSmid: viewportConfiguration?.stack?.smid,
    viewType: viewportConfiguration?.viewType,
    viewportId,
  });
}

// @ts-expect-error [EN-10263] - TS2322 - Type 'RecoilState<never[]>' is not assignable to type 'RecoilState<VtkAnnotation[]>'.
export const annotationsCacheState: RecoilState<Array<VtkAnnotation>> = atom({
  key: 'annotationsCacheState',
  default: [],
  effects: [broadcastChannelSynchronizerEffect({ unidirectional: true })],
});

export const segmentsForAnnotationSelector: (
  annotationId?: Nullable<string>
) => RecoilValueReadOnly<ReadonlyArray<Segment>> = selectorFamily({
  key: 'segmentsForAnnotationByIdSelector',
  get:
    (annotationId?: string | null) =>
    ({ get }) => {
      const annotations = get(annotationsCacheState);
      return annotations.find((annotation) => annotation.id === annotationId)?.segments ?? [];
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const annotationForIdSelector: (
  annotationId: Nullable<string>
) => RecoilValueReadOnly<Nullable<VtkAnnotation>> = selectorFamily({
  key: 'annotationForId',
  get:
    (annotationId: Nullable<string>) =>
    ({ get }) => {
      const annotations = get(annotationsCacheState);
      return annotations.find((annotation) => annotation.id === annotationId);
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const annotationsForSeriesSmids: (
  seriesSmids: string[]
) => RecoilValueReadOnly<VtkAnnotation[]> = selectorFamily({
  key: 'annotationsForSeriesSmids',
  get:
    (seriesSmids: string[]) =>
    ({ get }) => {
      const annotations = get(annotationsCacheState);

      // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      return annotations.filter((annotation) => seriesSmids.includes(annotation.seriesSmid));
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const studySuvParameters: (
  seriesSmids: string[]
) => RecoilValueReadOnly<Nullable<ScaleFactorTags>> = selectorFamily({
  key: 'studySuvParameters',
  get:
    (seriesSmids: string[]) =>
    ({ get }) => {
      const annotationsForStudy = get(annotationsForSeriesSmids(seriesSmids));

      return annotationsForStudy.find((annotation) => annotation.suv != null)?.suv;
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});
export const presentationStateAnnotationExistsForListOfSeriesSmidsSelector: (params: {
  seriesSmids?: ReadonlyArray<string>;
  sopInstanceUID?: string;
}) => RecoilValueReadOnly<boolean> = selectorFamily({
  key: 'presentationStateAnnotationExistsForListOfSeriesSmidsSelector',
  get:
    (params: { seriesSmids?: ReadonlyArray<string>; sopInstanceUID?: string }) =>
    ({ get }) => {
      if (params.seriesSmids == null || params.seriesSmids.length === 0) return false;
      const annotations = get(annotationsCacheState);
      const hasAnnotation = annotations
        .filter((annotation) =>
          params.sopInstanceUID != null
            ? annotation.metadata.sopInstanceUID === params.sopInstanceUID
            : true
        )
        .some(
          (annotation) =>
            // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
            params.seriesSmids.includes(annotation.seriesSmid) && annotation.source === 'GSPS'
        );
      return hasAnnotation;
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const usePresentationStateAnnotationExistsForListOfSeriesSmids = (
  seriesSmids?: ReadonlyArray<string>,
  sopInstanceUID?: string
): boolean => {
  const selector = presentationStateAnnotationExistsForListOfSeriesSmidsSelector({
    seriesSmids,
    sopInstanceUID,
  });
  const annotationsExist = useRecoilValue(selector);
  return annotationsExist;
};
